/**
 * Zuschauersicht einer Umfrage, wie sie das event-backend über den Socket /polls schickt
 * (services/pollProjection.js). Was hier ankommt, ist bereits auf den Stream-Delay
 * umgerechnet: eine Umfrage taucht erst auf, wenn sie im Stream angekündigt wird.
 */
export interface ViewerPoll {
  id: string;
  /** scale: ein Wert auf einer Zahlenskala, z. B. 1 bis 10; text: freie Antwort */
  type: 'single' | 'multiple' | 'scale' | 'text';
  /** Nur bei multiple; null = beliebig viele */
  maxChoices: number | null;
  defaultLang: string;
  question: Record<string, string>;
  /** Bei einer Skala eine Option je Wert, mit dem Zahlenwert in value */
  options: { id: string; label: Record<string, string>; value?: number }[];
  /** Nur bei scale: Wertebereich und Beschriftung der beiden Enden */
  scale?: PollScale | null;
  /** Teil einer Kette: Frage index (ab 1) von total */
  series?: PollSeries | null;
  status: 'open' | 'closed';
  /** live = nach der eigenen Stimme, onPublish = wenn die Regie es zeigt, never = gar nicht */
  resultsVisibility: 'live' | 'onPublish' | 'never';
  /**
   * Endergebnis: Balken oder Kreisdiagramm, während der Abstimmung immer Balken.
   * Skala: bars = Durchschnitt mit Verteilung, average = nur Durchschnitt.
   */
  resultsChart?: 'bars' | 'donut' | 'average';
  resultsVisible: boolean;
  /** Geschlossen, Ergebnis kommt noch */
  resultsPending: boolean;
  results: PollResults | null;
  openedAt: number;
}

/**
 * Umfrage fürs VoD (GET /polls/vod, nur nach dem Event): Inhalt wie live, dazu die Stellen im
 * Video je Sprache (Sekunden) — questionAt: dort wurde die Frage gestellt, resultAt: ab dort ist
 * das Ergebnis zu sehen (null = keins). Abgestimmt wird im VoD nicht.
 */
export interface VodPoll extends Pick<ViewerPoll, 'id' | 'type' | 'maxChoices' | 'defaultLang' | 'question' | 'options' | 'scale' | 'series' | 'resultsChart'> {
  questionAt: Record<string, number>;
  resultAt: Record<string, number> | null;
  results: PollResults | null;
}

export interface PollSeries {
  id: string;
  /** Titel der Kette — benennt ihren Tab */
  title?: string;
  index: number;
  total: number;
  /** stepwise: eine Frage nach der anderen; all: alle Fragen auf einmal */
  mode?: 'stepwise' | 'all';
}

/** Eigene Stimme: Auswahl, freie Antwort oder übersprungen */
export interface MyVote {
  optionIds: string[];
  text?: string;
  skipped?: boolean;
}

/** Was abgeschickt wird */
export type VotePayload = { optionIds: string[] } | { text: string } | { skip: true };

/** Eintrag der Umfrage-Ansicht: eine Umfrage oder eine ganze Kette (Fragen in ihrer Reihenfolge) */
export type PollItem =
  | { kind: 'poll'; id: string; poll: ViewerPoll }
  | { kind: 'chain'; id: string; polls: ViewerPoll[] };

export interface PollScale {
  min: number;
  max: number;
  minLabel: Record<string, string>;
  maxLabel: Record<string, string>;
  /** Nur bei gerader Spannweite (z. B. -5 bis 5): Beschriftung des Mittelpunkts */
  midLabel?: Record<string, string>;
}

export interface PollResults {
  counts: Record<string, number>;
  voterCount: number;
}

export interface PollsState {
  serverNow: number;
  /** Umfragen für das Event eingeschaltet — der Tab erscheint dann auch ohne Umfrage */
  enabled?: boolean;
  polls: ViewerPoll[];
}

export interface PollCounts {
  polls: ({ id: string } & PollResults)[];
}

export type VoteOutcome = 'ok' | 'closed' | 'login' | 'error';

/** Text in der Sprache des Zuschauers, sonst in der Standardsprache der Umfrage */
export function pollText(text: Record<string, string> | undefined, lang: string, defaultLang: string): string {
  if (!text) return '';
  return text[lang] || text[defaultLang] || Object.values(text).find(Boolean) || '';
}

const TAB_LABEL_LENGTH = 28;

const shorten = (text: string) =>
  text.length > TAB_LABEL_LENGTH ? `${text.slice(0, TAB_LABEL_LENGTH - 1).replace(/\s+$/, '')}…` : text;

/** Gekürzte Frage als Tab-Beschriftung; die volle Frage steht im title */
export function pollTabLabel(poll: ViewerPoll, lang: string): string {
  return shorten(pollText(poll.question, lang, poll.defaultLang));
}

/** Tab eines Eintrags: die Frage, bei einer Kette ihr Titel (ohne Titel: fallback, z. B. „Fragenreihe") */
export function pollItemLabel(item: PollItem, lang: string, fallback: string): string {
  if (item.kind === 'poll') return pollTabLabel(item.poll, lang);
  return shorten(item.polls[0]?.series?.title?.trim() || fallback);
}

/**
 * Ketten zu einem Eintrag zusammenfassen: an der Stelle ihrer vordersten Frage, mit den
 * Fragen in ihrer Reihenfolge. Die übrigen Umfragen bleiben einzeln.
 */
export function groupPolls(polls: ViewerPoll[]): PollItem[] {
  const items: PollItem[] = [];
  const chains = new Map<string, ViewerPoll[]>();
  for (const poll of polls) {
    const seriesId = poll.series?.id;
    if (!seriesId) {
      items.push({ kind: 'poll', id: poll.id, poll });
    } else if (chains.has(seriesId)) {
      chains.get(seriesId)!.push(poll);
    } else {
      const members = [poll];
      chains.set(seriesId, members);
      items.push({ kind: 'chain', id: `chain:${seriesId}`, polls: members });
    }
  }
  for (const members of chains.values()) members.sort((a, b) => (a.series?.index ?? 0) - (b.series?.index ?? 0));
  return items;
}

export const itemPolls = (item: PollItem): ViewerPoll[] => (item.kind === 'poll' ? [item.poll] : item.polls);
